import { motion } from 'framer-motion';

import { Button, Card, cn } from '@llura/ui';
import { AnalyticsEvents, FlowSteps } from '@llura/constants';

import {
  BOUNDARY_CATEGORIES,
  useCovenantStore,
  type BoundaryCategory,
  type BoundaryLevel,
} from '../../../stores/covenant.store';
import { trackEvent, useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';

const LEVELS: readonly BoundaryLevel[] = ['soft', 'firm', 'pushing'] as const;

interface CovenantStepProps {
  onConfirm: () => void;
}

export function CovenantStep({ onConfirm }: CovenantStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorCovenant);
  const { strings, interp } = useStrings();
  const boundaries = useCovenantStore((s) => s.boundaries);
  const setBoundary = useCovenantStore((s) => s.setBoundary);
  const acceptCovenant = useCovenantStore((s) => s.acceptCovenant);

  const stepLabel = interp(strings.onboarding.stepLabel, {
    current: 4,
    total: strings.onboarding.totalSteps,
  });

  const confirm = () => {
    acceptCovenant();
    trackEvent(AnalyticsEvents.CovenantConfirmed, {});
    onConfirm();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-6"
    >
      <header className="space-y-2 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{stepLabel}</p>
        <h2 className="font-serif text-3xl">{strings.onboarding.covenant.title}</h2>
        <p className="text-sm text-muted-foreground">{strings.onboarding.covenant.hint}</p>
      </header>

      <div className="space-y-3">
        {BOUNDARY_CATEGORIES.map((category) => (
          <CategoryRow
            key={category}
            category={category}
            value={boundaries[category]}
            onChange={(level) => setBoundary(category, level)}
          />
        ))}
      </div>

      <Button
        onClick={confirm}
        size="lg"
        className="h-12 w-full rounded-full bg-primary text-primary-foreground glow-primary"
      >
        {strings.onboarding.covenant.confirmCta}
      </Button>
    </motion.div>
  );
}

interface CategoryRowProps {
  category: BoundaryCategory;
  value: BoundaryLevel;
  onChange: (level: BoundaryLevel) => void;
}

function CategoryRow({ category, value, onChange }: CategoryRowProps) {
  const { strings } = useStrings();
  const meta = strings.onboarding.covenant.categories[category];

  return (
    <Card className="space-y-3 border-border bg-card p-4">
      <div className="space-y-0.5">
        <div className="font-medium leading-tight">{meta.label}</div>
        <div className="text-xs text-muted-foreground">{meta.description}</div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {LEVELS.map((level) => {
          const active = value === level;
          return (
            <button
              key={level}
              type="button"
              onClick={() => onChange(level)}
              className={cn(
                'rounded-full border px-2 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-all',
                active
                  ? 'border-primary bg-primary/15 text-foreground'
                  : 'border-border bg-transparent text-muted-foreground hover:border-primary/40',
              )}
            >
              {strings.onboarding.covenant.levels[level]}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
